import React, {useCallback, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import toast from "react-hot-toast";

import {classNames} from "../../../utils/Utils";
import InputToggle from "../../../components/common/form/input-toggle";
import UnderlineTab from "../../../components/partials/Tabs/underline-tab";
import CustomerCell from "../../../components/common/table/customer-cell";
import {fetchCurrencies, fetchTermTypes} from "../../../reducers/configReducer";
import {hasPermissionFor} from "../../../components/partials/restricted/base-permission-wrapper";
import {permissionGroup, permissionSpecific} from "../../../constants/permissions";

import DocumentForm from "./document-form";
import DeliveryForm from "./document-delivery-form";
import Field from "./document-header-field";
import ExpandableBox from "./document-header-expandable-box";
import {
    AUTO_SAVE_TOAST_ID,
    FALLBACK_VALUE,
    getDisplayValue,
    HEADER_COMPACT_CLASS,
    buildAddress,
    mergeDocumentData,
    sanitizeFormValues,
    stripHtml,
} from "./document-header-utils";

const extractCustomerInfo = (document) => {
    if (!document) {
        return {};
    }

    const customer = document.customer
        ?? document.company_document?.connection
        ?? document.customer_company
        ?? document.company
        ?? null;

    const contact = document.customer_contact
        ?? document.contact
        ?? customer?.contact
        ?? null;

    const customerName = customer?.business_name
        ?? document.customer_name
        ?? customer?.company_name
        ?? customer?.name
        ?? document.company_name
        ?? null;

    const contactName = contact
        ? [contact.firstname, contact.lastname, contact.name]
            .filter(Boolean)
            .join(" ")
        : document.contact_name ?? null;

    const email = customer?.importer_email
        ?? contact?.email
        ?? document.customer_email
        ?? null;

    const reference = customer?.importer_reference
        ?? document.internal_reference
        ?? document.document_reference
        ?? null;

    const vatEntity = typeof document.company_vat === "object" ? document.company_vat : null;
    const vatCode = vatEntity?.name
        ?? vatEntity?.code
        ?? (vatEntity?.value !== undefined && vatEntity?.value !== null ? `${vatEntity.value}%` : null)
        ?? customer?.importer_vat_code
        ?? document.company_vat
        ?? document.vat_code
        ?? null;

    const paymentTermEntityRaw = document.company_payment_terms
        ?? document.company_payment_term
        ?? null;
    const paymentTermEntity = typeof paymentTermEntityRaw === "object"
        ? paymentTermEntityRaw
        : null;
    const paymentTermsRaw = paymentTermEntity?.name
        ?? customer?.payment_terms
        ?? document.payment_terms
        ?? null;

    const paymentTerms = paymentTermsRaw ? stripHtml(paymentTermsRaw) : null;

    const language = customer?.importer_language?.name
        ?? customer?.importer_language?.code
        ?? null;

    const currency = customer?.importer_currency?.code
        ?? (typeof document.currency === "object" ? document.currency?.code : document.currency)
        ?? null;

    const address = buildAddress({
        address: customer?.importer_address ?? customer?.address ?? document.customer_address,
        zip_code: customer?.importer_zip_code ?? document.customer_zip,
        city: customer?.importer_city ?? document.customer_city,
        country: customer?.importer_country?.name
            ?? customer?.importer_country?.code
            ?? document.customer_country,
    });

    return {
        customerName,
        contactName,
        email,
        reference,
        vatCode,
        paymentTerms: paymentTerms || null,
        language,
        currency,
        address,
        color: customer?.color ?? null,
    };
};

const DocumentHeader = ({
    document,
    onEdit,
    onUpdate,
    companyId,
    onAutoSaveStateChange,
    canEdit,
    canEditConnectionVisibility,
}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {company, permissions} = useSelector((state) => state.userCompany);
    const {
        currenciesList,
        isCurrenciesListLoading,
        termTypesList,
        isTermTypesListLoading,
    } = useSelector((state) => state.config);

    const [currentDocument, setCurrentDocument] = useState(document ?? null);
    const [isDetailsSubmitting, setDetailsSubmitting] = useState(false);
    const [isDeliverySubmitting, setDeliverySubmitting] = useState(false);
    const [activeFormTab, setActiveFormTab] = useState("document");
    const [isConnectionVisibilityUpdating, setConnectionVisibilityUpdating] = useState(false);
    const [connectionVisibilityRevision, setConnectionVisibilityRevision] = useState(0);
    const canUpdateDocument = canEdit && typeof onUpdate === "function";
    const canEditConnectionToggle = canEditConnectionVisibility ?? canEdit;

    useEffect(() => {
        if (typeof onAutoSaveStateChange === "function") {
            onAutoSaveStateChange(
                isDetailsSubmitting
                || isDeliverySubmitting
                || isConnectionVisibilityUpdating
            );
        }
    }, [
        isDetailsSubmitting,
        isDeliverySubmitting,
        isConnectionVisibilityUpdating,
        onAutoSaveStateChange,
    ]);

    useEffect(() => () => {
        if (typeof onAutoSaveStateChange === "function") {
            onAutoSaveStateChange(false);
        }
    }, [onAutoSaveStateChange]);

    const formTabs = useMemo(() => ([
        {
            name: t("document.section.document", {defaultValue: "Documento"}),
            group: "document",
        },
        {
            name: t("document.section.delivery", {defaultValue: "Consegna & Destinazione"}),
            group: "delivery",
        },
    ]), [t]);

    const isActiveVat = hasPermissionFor(
        permissions,
        permissionGroup.COMPANY_CONFIGURATION,
        permissionSpecific.COMPANY_CONF_VAT
    );
    const isActivePaymentTerms = hasPermissionFor(
        permissions,
        permissionGroup.COMPANY_CONFIGURATION,
        permissionSpecific.COMPANY_CONF_PAYMENT_TERMS
    );
    const isActiveWarehouse = hasPermissionFor(
        permissions,
        permissionGroup.COMPANY_CONFIGURATION,
        permissionSpecific.COMPANY_CONF_WAREHOUSE
    );

    useEffect(() => {
        if (!Array.isArray(currenciesList) || currenciesList.length === 0) {
            dispatch(fetchCurrencies());
        }
        if (!Array.isArray(termTypesList) || termTypesList.length === 0) {
            dispatch(fetchTermTypes());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, currenciesList?.length, termTypesList?.length]);

    useEffect(() => {
        setCurrentDocument(document ?? null);
    }, [document]);

    const resolvedDocumentId = useMemo(() => (
        currentDocument?.id
        ?? currentDocument?.document_id
        ?? currentDocument?.documentId
        ?? null
    ), [currentDocument]);

    const resolvedCompanyId = useMemo(() => (
        companyId
        ?? currentDocument?.company?.id
        ?? currentDocument?.company_id
        ?? currentDocument?.company_document?.company_id
        ?? null
    ), [companyId, currentDocument]);

    const resolvedConnectionId = useMemo(() => {
        const candidates = [
            currentDocument?.connection,
            currentDocument?.company_document?.connection,
            currentDocument?.customer,
            currentDocument?.customer_company,
        ];

        const candidateWithId = candidates.find((candidate) => candidate?.id);
        if (candidateWithId?.id) {
            return candidateWithId.id;
        }

        return currentDocument?.connection_id
            ?? currentDocument?.customer_id
            ?? null;
    }, [currentDocument]);

    const handleSubmitWithState = useCallback(async ({payload, merge}, setSubmitting) => {
        const basePayload = payload ?? {};
        const payloadWithId = resolvedDocumentId
            ? {...basePayload, id: resolvedDocumentId}
            : {...basePayload};
        const normalizedPayload = sanitizeFormValues(payloadWithId);
        if (!canUpdateDocument) {
            toast.error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
            setSubmitting(false);
            return;
        }
        setSubmitting(true);
        try {
            let response = null;
            if (typeof onUpdate === "function") {
                response = await onUpdate(normalizedPayload);
            }

            if (response) {
                setCurrentDocument(response);
            } else if (merge) {
                setCurrentDocument((prev) => mergeDocumentData(prev, merge));
            } else {
                setCurrentDocument((prev) => mergeDocumentData(prev, normalizedPayload));
            }

            toast.success(t("app.document.update_success", {defaultValue: "Documento aggiornato"}), {id: AUTO_SAVE_TOAST_ID});
        } catch (error) {
            toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
        } finally {
            setSubmitting(false);
        }
    }, [canUpdateDocument, onUpdate, resolvedDocumentId, t]);

    const handleDetailsSubmit = useCallback((data) => handleSubmitWithState(data, setDetailsSubmitting), [handleSubmitWithState]);

    const handleDeliverySubmit = useCallback(async ({payload, merge}) => {
        const basePayload = payload ?? {};
        const sanitizedPayload = sanitizeFormValues(basePayload);
        const payloadWithId = resolvedDocumentId
            ? {...sanitizedPayload, id: resolvedDocumentId}
            : {...sanitizedPayload};
        if (!canUpdateDocument) {
            toast.error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
            return;
        }
        setDeliverySubmitting(true);
        try {
            let response = null;
            if (typeof onUpdate === "function") {
                response = await onUpdate(payloadWithId);
            }

            if (response) {
                setCurrentDocument(response);
            } else {
                const mergePayload = merge ?? sanitizedPayload;
                setCurrentDocument((prev) => mergeDocumentData(prev, mergePayload));
            }

            toast.success(t("app.document.update_success", {defaultValue: "Documento aggiornato"}), {id: AUTO_SAVE_TOAST_ID});
        } catch (error) {
            toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
        } finally {
            setDeliverySubmitting(false);
        }
    }, [canUpdateDocument, onUpdate, resolvedDocumentId, t]);

    const handleConnectionVisibilityChange = useCallback(async (nextValue) => {
        const resolvedNextValue = Boolean(nextValue);
        const previousValue = Boolean(currentDocument?.connection_can_view);

        if (resolvedNextValue === previousValue || isConnectionVisibilityUpdating) {
            return;
        }

        if (!canEditConnectionToggle || typeof onUpdate !== "function") {
            toast.error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
            return;
        }

        setConnectionVisibilityUpdating(true);

        const basePayload = {connection_can_view: resolvedNextValue};
        const payloadWithId = resolvedDocumentId
            ? {...basePayload, id: resolvedDocumentId}
            : basePayload;
        const normalizedPayload = sanitizeFormValues(payloadWithId);

        try {
            let response = null;
            if (typeof onUpdate === "function") {
                response = await onUpdate(normalizedPayload);
            }

            if (response) {
                setCurrentDocument(response);
            } else {
                setCurrentDocument((prev) => mergeDocumentData(prev, {connection_can_view: resolvedNextValue}));
            }

            toast.success(t("app.document.update_success", {defaultValue: "Documento aggiornato"}), {id: AUTO_SAVE_TOAST_ID});
        } catch (error) {
            toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
            setConnectionVisibilityRevision((value) => value + 1);
        } finally {
            setConnectionVisibilityUpdating(false);
        }
    }, [
        currentDocument?.connection_can_view,
        isConnectionVisibilityUpdating,
        canEditConnectionToggle,
        onUpdate,
        resolvedDocumentId,
        t,
    ]);

    const connectionVisibility = currentDocument?.connection_can_view;
    const resolvedConnectionVisibility = Boolean(connectionVisibility);

    const customerInfo = useMemo(() => {
        const info = extractCustomerInfo(currentDocument);
        return {
            ...info,
            customerName: info.customerName ?? FALLBACK_VALUE,
        };
    }, [currentDocument]);

    const customerEntity = useMemo(() => {
        const baseCustomer = currentDocument?.customer
            ?? currentDocument?.company_document?.connection
            ?? currentDocument?.customer_company
            ?? currentDocument?.company
            ?? null;

        if (!baseCustomer) {
            return {
                business_name: customerInfo.customerName,
                color: customerInfo.color ?? null,
            };
        }

        return {
            ...baseCustomer,
            business_name: baseCustomer.business_name
                ?? baseCustomer.name
                ?? customerInfo.customerName,
            color: baseCustomer.color ?? customerInfo.color ?? null,
        };
    }, [currentDocument, customerInfo.customerName, customerInfo.color]);

    const customerFields = useMemo(() => ([
        {
            id: "customer-info",
            className: "sm:col-span-2",
            render: () => (
                <>
                    <p className="text-xs font-semibold uppercase tracking-wide text-blue-1000">CLIENTE</p>
                    <div className="mt-1.5">
                        <CustomerCell
                            customer={customerEntity}
                            extra={customerInfo.email}
                            secondExtra={customerInfo.contactName}
                        />
                    </div>
                </>
            ),
        },
        {
            id: "reference",
            label: t("document.form.customer_reference", {defaultValue: "Riferimento cliente"}),
            value: customerInfo.reference,
            className: "sm:col-span-1",
        },
        {
            id: "vat-code",
            label: t("document.form.company_vat", {defaultValue: "IVA"}),
            value: customerInfo.vatCode,
            className: "sm:col-span-1",
        },
        {
            id: "language",
            label: t("document.form.language", {defaultValue: "Lingua"}),
            value: customerInfo.language,
            className: "sm:col-span-1",
        },
        {
            id: "currency",
            label: t("document.form.currency", {defaultValue: "Valuta"}),
            value: customerInfo.currency,
            className: "sm:col-span-1",
        },
        {
            id: "email",
            label: t("document.form.email", {defaultValue: "Email"}),
            value: customerInfo.email,
            className: "sm:col-span-2",
        },
        {
            id: "connection-can-view-toggle",
            className: "sm:col-span-2",
            render: () => (
                <InputToggle
                    key={`connection-can-view-${connectionVisibilityRevision}`}
                    label={t("document.form.connection_can_view", {defaultValue: "Visibile al cliente"})}
                    description={t("document.form.connection_can_view_description", {defaultValue: "Consenti al cliente di accedere a questo documento dal portale."})}
                    defaultChecked={resolvedConnectionVisibility}
                    onChange={canEditConnectionToggle ? handleConnectionVisibilityChange : undefined}
                    isDisabled={!canEditConnectionToggle || isConnectionVisibilityUpdating}
                    size="compact"
                />
            ),
        },
    ]), [
        connectionVisibilityRevision,
        customerEntity,
        customerInfo,
        handleConnectionVisibilityChange,
        canEditConnectionToggle,
        resolvedConnectionVisibility,
        isConnectionVisibilityUpdating,
        t,
    ]);

    const mobilePrimaryField = customerFields.find((field) => field.id === "customer-info") ?? null;
    const mobileToggleField = customerFields.find((field) => field.id === "connection-can-view-toggle") ?? null;
    const mobileSecondaryFields = customerFields.filter(({id}) => id !== "customer-info" && id !== "connection-can-view-toggle");
    const activeFormContent = useMemo(() => {
        if (activeFormTab === "document") {
            return (
                <DocumentForm
                    document={currentDocument}
                    onSubmit={handleDetailsSubmit}
                    isSubmitting={isDetailsSubmitting}
                    termTypesList={termTypesList}
                    isTermTypesListLoading={isTermTypesListLoading}
                    currenciesList={currenciesList}
                    isCurrenciesListLoading={isCurrenciesListLoading}
                    company={company}
                    companyId={resolvedCompanyId ?? companyId}
                    isEditable={canEdit}
                    isVatEnabled={isActiveVat}
                    isPaymentTermsEnabled={isActivePaymentTerms}
                    isWarehouseEnabled={isActiveWarehouse}
                />
            );
        }

        if (activeFormTab === "delivery") {
            return (
                <DeliveryForm
                    document={currentDocument}
                    onSubmit={handleDeliverySubmit}
                    isSubmitting={isDeliverySubmitting}
                    companyId={resolvedCompanyId}
                    connectionId={resolvedConnectionId}
                    isEditable={canEdit}
                />
            );
        }

        return null;
        // eslint-disable-next-line
    }, [
        activeFormTab,
        currentDocument,
        handleDetailsSubmit,
        isDetailsSubmitting,
        termTypesList,
        isTermTypesListLoading,
        currenciesList,
        isCurrenciesListLoading,
        company,
        resolvedCompanyId,
        companyId,
        canEdit,
        handleDeliverySubmit,
        isDeliverySubmitting,
        resolvedConnectionId,
    ]);

    if (!currentDocument) {
        return null;
    }

    const summaryHeadingId = `document-header-summary-${currentDocument?.id ?? "default"}`;
    const customerHeadingId = `${summaryHeadingId}-customer`;
    const documentHeadingId = `${summaryHeadingId}-document`;
    const summarySectionLabel = t("document.header.summary_list", {defaultValue: "Panoramica documento"});
    const customerSectionLabel = t("document.header.customer_summary", {defaultValue: "Cliente"});
    const documentSectionLabel = t("document.header.document_summary", {defaultValue: "Dettagli documento"});

    const isSaving = isDetailsSubmitting || isDeliverySubmitting || isConnectionVisibilityUpdating;

    return (
        <div className="relative" aria-busy={isSaving ? "true" : undefined}>
            <h2 id={summaryHeadingId} className="sr-only">
                {summarySectionLabel}
            </h2>
            <ul
                aria-labelledby={summaryHeadingId}
                className={classNames("mx-auto grid w-full grid-cols-1 gap-0  sm:grid-cols-2 lg:grid-cols-4 ", HEADER_COMPACT_CLASS)}
            >
                <ExpandableBox
                    id="customer"
                    className="lg:col-span-1 xl:col-span-1"
                    ariaLabel={customerSectionLabel}
                    ariaLabelledBy={customerHeadingId}
                >
                    <>
                        <h3 id={customerHeadingId} className="sr-only">
                            {customerSectionLabel}
                        </h3>

                        <div className={classNames("space-y-4 p-3 sm:hidden", HEADER_COMPACT_CLASS)}>
                            {mobilePrimaryField && (
                                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                    {mobilePrimaryField.render
                                        ? mobilePrimaryField.render()
                                        : (
                                            <>
                                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                    {mobilePrimaryField.label}
                                                </p>
                                                <p className="mt-1 text-sm font-medium text-slate-900">
                                                    {getDisplayValue(mobilePrimaryField.value)}
                                                </p>
                                            </>
                                        )}
                                </div>
                            )}

                            {mobileSecondaryFields.length > 0 && (
                                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                                    <dl className="divide-y divide-slate-100" aria-label={t("document.header.additional_customer_details", {defaultValue: "Altri dettagli cliente"})}>
                                        {mobileSecondaryFields.map((field) => (
                                            <div key={field.id} className="px-4 py-3">
                                                {field.render
                                                    ? field.render()
                                                    : (
                                                        <>
                                                            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                                {field.label}
                                                            </dt>
                                                            <dd className="mt-1 text-sm font-medium text-slate-900">
                                                                {getDisplayValue(field.value)}
                                                            </dd>
                                                        </>
                                                    )}
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                            )}

                            {mobileToggleField && (
                                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                    {mobileToggleField.render
                                        ? mobileToggleField.render()
                                        : (
                                            <>
                                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                                    {mobileToggleField.label}
                                                </p>
                                                <p className="mt-1 text-sm font-medium text-slate-900">
                                                    {getDisplayValue(mobileToggleField.value)}
                                                </p>
                                            </>
                                        )}
                                </div>
                            )}
                        </div>

                        <div className={classNames("hidden grid-cols-1 gap-2.5 sm:grid sm:grid-cols-2 p-3 sm:p-4", HEADER_COMPACT_CLASS)}>
                            {customerFields.map((field) => (
                                field.render
                                    ? (
                                        <div key={field.id} className={classNames("", field.className)}>
                                            {field.render()}
                                        </div>
                                    )
                                    : (
                                        <Field
                                            key={field.id}
                                            label={field.label}
                                            value={field.value}
                                            className={field.className}
                                        />
                                    )
                            ))}
                        </div>
                    </>
                </ExpandableBox>

                <ExpandableBox
                    id="document"
                    className="lg:col-span-3 xl:col-span-3"
                    onEdit={onEdit}
                    ariaLabel={documentSectionLabel}
                    ariaLabelledBy={documentHeadingId}
                >
                    <>
                        <h3 id={documentHeadingId} className="sr-only">
                            {documentSectionLabel}
                        </h3>

                        <div className={classNames("sm:hidden space-y-4 p-3", HEADER_COMPACT_CLASS)}>
                            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                                <div className="rounded-t-2xl border-b border-slate-100 bg-slate-50 py-3">
                                    <UnderlineTab
                                        tabs={formTabs}
                                        activeGroup={activeFormTab}
                                        setActiveGroup={setActiveFormTab}
                                        extraClass="px-3"
                                        marginTop="mt-0"
                                        baseClass="mb-0"
                                        hasBorder={false}
                                    />
                                </div>
                                <div className="space-y-4 p-4 text-sm text-slate-900">
                                    {activeFormContent}
                                </div>
                            </div>
                        </div>

                        <div className={classNames("hidden sm:block", HEADER_COMPACT_CLASS)}>
                            <div className="space-y-3 text-sm text-slate-900 pt-3">
                                <UnderlineTab
                                    tabs={formTabs}
                                    activeGroup={activeFormTab}
                                    setActiveGroup={setActiveFormTab}
                                    extraClass="px-3"
                                    marginTop="mt-0"
                                    baseClass="mb-2"
                                    hasBorder={true}
                                />
                                <div className="p-3 space-y-3">
                                    {activeFormContent}
                                </div>
                            </div>
                        </div>
                    </>
                </ExpandableBox>
            </ul>
            {isSaving && (
                <div className="pointer-events-auto absolute inset-0 z-[60] flex items-center justify-center bg-white/60 backdrop-blur-sm">
                    <div className="rounded-md bg-gray-900/80 px-3 py-2 text-sm font-medium text-white shadow">
                        {t("document.form.auto_save_in_progress", {defaultValue: "Salvataggio in corso..."})}
                    </div>
                </div>
            )}
        </div>
    );
};

DocumentHeader.propTypes = {
    document: PropTypes.object,
    onEdit: PropTypes.func,
    onUpdate: PropTypes.func,
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onAutoSaveStateChange: PropTypes.func,
    canEdit: PropTypes.bool,
    canEditConnectionVisibility: PropTypes.bool,
};

DocumentHeader.defaultProps = {
    document: null,
    onEdit: null,
    onUpdate: null,
    companyId: null,
    onAutoSaveStateChange: null,
    canEdit: true,
    canEditConnectionVisibility: undefined,
};

export default React.memo(DocumentHeader);
